import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Lightbulb, 
  Sparkles,
  User,
  Video,
  Code,
  Palette,
  ExternalLink,
  Copy,
  Check,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  useUserCloud, 
  EmailCaptureModal,
  EmergentCTA,
  AboutEmergent,
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = {
  id: 'more-prompts',
  type: 'prompt',
  platform: 'Website',
  title: 'More Prompt Ideas',
  description: 'A collection of prompt starters for different use cases and industries.',
  color: '#FFD700',
  author: {
    name: 'techbaenae',
    handle: '@techbaenae',
    avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
    title: 'Data Dreamer'
  },
};

// Prompt table categories
const promptCategories = [
  {
    id: 'personal-brand',
    title: 'For Personal Brand Websites',
    icon: User,
    color: '#6A0DAD',
    prompts: [
      { feature: 'Animated hero text', starter: '"Add a typewriter effect to my hero headline that types out different words"' },
      { feature: 'Scroll animations', starter: '"Add fade-in animations as I scroll down the page"' },
      { feature: 'Project case studies', starter: '"Create a detailed case study template with problem/solution/results sections"' },
      { feature: 'Booking integration', starter: '"Add a Calendly embed for booking calls with me"' },
      { feature: 'Pricing table', starter: '"Add a pricing section with 3 tiers for my services"' },
      { feature: 'FAQ section', starter: '"Add an expandable FAQ section with these questions: [LIST]"' },
      { feature: 'Press kit page', starter: '"Create a /press page with my bio, photos, and press mentions"' },
      { feature: 'Resume/CV page', starter: '"Create a beautiful online resume at /resume"' },
    ]
  },
  {
    id: 'creators',
    title: 'For Creators & Influencers',
    icon: Video,
    color: '#FF007F',
    prompts: [
      { feature: 'Link in bio page', starter: '"Create a links page like Linktree but on my own domain"' },
      { feature: 'Media kit', starter: '"Create a media kit page showing my audience stats and collaboration info"' },
      { feature: 'Merch store link', starter: '"Add a \'Shop\' section that links to my merch store at [URL]"' },
      { feature: 'Podcast player', starter: '"Embed my latest podcast episodes from [SPOTIFY/APPLE]"' },
      { feature: 'Instagram feed', starter: '"Show my latest Instagram posts on my website"' },
      { feature: 'YouTube embeds', starter: '"Create a video gallery with my latest YouTube uploads"' },
      { feature: 'Newsletter signup', starter: '"Add a prominent newsletter signup form connected to [BEEHIIV/SUBSTACK]"' },
      { feature: 'Collaboration form', starter: '"Add a form for brands to submit collaboration inquiries"' },
    ]
  },
  {
    id: 'developers',
    title: 'For Developers & Tech',
    icon: Code,
    color: '#00CED1',
    prompts: [
      { feature: 'GitHub projects', starter: '"Pull my pinned repos from GitHub and display them"' },
      { feature: 'Tech stack icons', starter: '"Add icons showing the technologies I work with"' },
      { feature: 'Code snippets', starter: '"Add syntax-highlighted code blocks to my portfolio pieces"' },
      { feature: 'API documentation', starter: '"Create a /docs page for my API"' },
      { feature: 'Terminal animation', starter: '"Add a terminal-style animation in my hero section"' },
      { feature: 'Live demo embeds', starter: '"Embed interactive demos of my projects using iframes"' },
      { feature: 'Contribution graph', starter: '"Show my GitHub contribution graph on my homepage"' },
      { feature: 'Blog with MDX', starter: '"Add a developer blog with MDX support for code blocks"' },
    ]
  },
  {
    id: 'artists',
    title: 'For Artists & Designers',
    icon: Palette,
    color: '#FF6B6B',
    prompts: [
      { feature: 'Masonry gallery', starter: '"Create a Pinterest-style masonry grid for my artwork"' },
      { feature: 'Lightbox viewer', starter: '"When I click an image, show it fullscreen with navigation"' },
      { feature: 'Before/after slider', starter: '"Add a before/after comparison slider for my work"' },
      { feature: 'Process showcase', starter: '"Show my design process with a step-by-step visual"' },
      { feature: 'Color palette display', starter: '"Display the color palettes used in each project"' },
      { feature: 'Artwork categories', starter: '"Add filterable categories for my portfolio (illustrations, logos, etc.)"' },
      { feature: 'Commission form', starter: '"Add a commission request form with budget and project type options"' },
      { feature: '3D model viewer', starter: '"Add a 3D model viewer for my product designs"' },
    ]
  },
];

// Troubleshooting prompts
const troubleshootingPrompts = [
  {
    title: 'Something isn\'t working',
    prompt: `Something isn't working right. Here's what I see:
[DESCRIBE THE ISSUE]

What I expected:
[WHAT SHOULD HAPPEN]

Please help me debug and fix this.`
  },
  {
    title: 'Mobile styling issues',
    prompt: `The styling looks off on mobile. Please check:
- [SPECIFIC SECTION]
- Make sure it's responsive
- Text should be readable
- Buttons should be tappable`
  },
  {
    title: 'Changes not showing',
    prompt: `My changes aren't showing up. Can you:
1. Check if there are any errors
2. Clear any caching
3. Verify the database is saving correctly`
  },
];

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      title="Copy prompt"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4 text-gray-400" />
      )}
    </button>
  );
};

// Prompt table component
const PromptTable = ({ category }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const Icon = category.icon;

  return (
    <div className="mb-8 rounded-2xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: `${category.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: category.color }} />
          </div>
          <h3 className="font-semibold text-gray-900">{category.title}</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-t border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt Starter</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {category.prompts.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <span className="font-medium text-gray-900">{item.feature}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-600 font-mono">{item.starter}</span>
                  </td>
                  <td className="px-3 py-4">
                    <CopyButton text={item.starter} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MorePromptsPage = () => {
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
  };

  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <Lightbulb className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              Prompt Collection
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        
        {/* How to use */}
        <div className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-100">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            How to Use These Prompts
          </h3>
          <ol className="space-y-2 text-sm text-gray-600">
            <li><strong>1. Find your feature</strong> — Browse the tables below for what you want to add</li>
            <li><strong>2. Copy the starter</strong> — Click the copy icon next to any prompt</li>
            <li><strong>3. Customize</strong> — Fill in the brackets [LIKE THIS] with your specific info</li>
            <li><strong>4. Build</strong> — Paste into Emergent and watch it come to life</li>
          </ol>
        </div>

        {/* Prompt Tables */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse by Category</h2>
        
        {promptCategories.map(category => (
          <PromptTable key={category.id} category={category} />
        ))}

        <EmergentCTA />

        {/* Troubleshooting Section */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">Troubleshooting Prompts</h2>
        <p className="text-gray-600 mb-6">Having issues? Use these prompts to get help debugging.</p>
        
        <div className="space-y-4 mb-12">
          {troubleshootingPrompts.map((item, i) => (
            <div key={i} className="p-5 rounded-xl border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{item.title}</h4>
                <CopyButton text={item.prompt} />
              </div>
              <pre className="text-sm text-gray-600 whitespace-pre-wrap font-mono bg-white p-3 rounded-lg border border-gray-100">
                {item.prompt}
              </pre>
            </div>
          ))}
        </div>

        {/* Questions */}
        <div className="mt-12 p-6 rounded-2xl bg-gray-50 text-center">
          <h3 className="font-bold text-gray-900 mb-2">Questions?</h3>
          <p className="text-gray-600 text-sm mb-4">
            Found these helpful? Tag me when you build your site! 🚀
          </p>
          <p className="text-gray-400 text-xs">Last updated: January 2026</p>
        </div>

        <AuthorCard author={resource.author} />

        <AboutEmergent />
      </div>
    </div>
  );
};

export default MorePromptsPage;
